import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { type Responses } from "@narmi/tile-events";
import { type RuntimeProps } from "./types";
import { useEventHandler } from "./hooks";

const STYLE = {
  border: "none",
} satisfies React.CSSProperties;

const WIDTH = "100%";

const Runtime = ({
  redirectUri,
  openFullView,
  onUnhandledMessage,
  onVersionFail,
  refreshAuthorization,
}: RuntimeProps) => {
  const tileOrigin = useMemo(() => new URL(redirectUri).origin, [redirectUri]);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const postMessage = useCallback(
    (data: Responses) => {
      iframeRef.current?.contentWindow?.postMessage(data, tileOrigin);
    },
    [tileOrigin]
  );

  const { initialized, height, handleEvent } = useEventHandler({
    postMessage,
    openFullView,
    onUnhandledMessage,
    onVersionFail,
    refreshAuthorization,
  });

  const webViewHeight = useMemo(
    () => (!initialized ? 0 : Math.ceil(height)),
    [initialized, height]
  );

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const listener = async (message: MessageEvent) => {
      const { data, source, origin } = message;
      if (iframe.contentWindow !== source || origin !== tileOrigin) {
        return;
      }
      handleEvent(data);
    };

    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [handleEvent, tileOrigin]);

  return (
    <iframe
      ref={iframeRef}
      src={redirectUri}
      width={WIDTH}
      height={webViewHeight}
      style={STYLE}
      referrerPolicy="no-referrer"
      allow=""
      sandbox="allow-scripts allow-same-origin"
    />
  );
};

export default memo(Runtime);
